import { array, bool, func, shape } from "prop-types"
import FilterBadges from "./FilterBadges"
import FilterDropdown from "./FilterDropdown"
import FilterInput, { SEARCH_KIND } from "./FilterInput"
import DateOptions, { DATE_KIND, dateBadge } from "./DateOptions"
import StatusOptions, { STATUS_KIND, statusBadge } from "./StatusOptions"
import BalanceOptions, { BALANCE_KIND, balanceBadge } from "./BalanceOptions"
import AddOnOptions, { ADD_ON_KIND, addOnBadge } from "./AddOnOptions"
import FormOptions, { FORM_KIND, formBadge } from "./FormOptions"
import AttendeeTypeOptions, { ATTENDEE_TYPE_KIND, attendeeTypeBadge } from "./AttendeeTypeOptions"
import AssignmentOptions, { ASSIGNMENT_KIND, assignmentBadge } from "./AssignmentOptions"
import AttendanceOptions, { ATTENDANCE_KIND, attendanceBadge } from "./AttendanceOptions"
import DiscountOptions, { DISCOUNT_KIND, discountBadge } from "./DiscountOptions"
import QuestionOptions, { QUESTION_KIND, questionBadge } from "./QuestionOptions"
import ScholarshipOptions, { SCHOLARSHIP_KIND, scholarshipBadge } from "./ScholarshipOptions"
import SignupTimeOptions, { signupTimeBadge, SIGNUP_TIME_KIND } from "./SignupTimeOptions"
import pendoTrackEvent from "utils/pendo_track_event"

const FilterBar = ({
  event,
  filterOptions,
  searchFilters,
  onFilterAdd,
  onFilterRemove,
  onFilterClear,
  waitlist
}) => {
  const filterMenuItems = {
    [ATTENDEE_TYPE_KIND]: {
      dropdown: {
        label: "Selection type",
        isShowing: true
      },
      badge: attendeeTypeBadge,
      component: AttendeeTypeOptions
    },
    [ASSIGNMENT_KIND]: {
      dropdown: {
        label: "Assignment",
        isShowing: !waitlist && filterOptions[ASSIGNMENT_KIND].length > 0
      },
      badge: assignmentBadge,
      component: AssignmentOptions
    },
    [DATE_KIND]: {
      dropdown: { label: "Date submitted", isShowing: true },
      badge: dateBadge,
      component: DateOptions
    },
    [SIGNUP_TIME_KIND]: {
      dropdown: {
        label: "Date registered for",
        isShowing: event.isRegisterForSeparateTimesEnabled
      },
      badge: signupTimeBadge,
      component: SignupTimeOptions
    },
    [STATUS_KIND]: {
      dropdown: { label: "Registration status", isShowing: !waitlist },
      badge: statusBadge,
      component: StatusOptions
    },
    [BALANCE_KIND]: {
      dropdown: { label: "Balance", isShowing: !waitlist && !event.isSimple },
      badge: balanceBadge,
      component: BalanceOptions
    },
    [ADD_ON_KIND]: {
      dropdown: {
        label: "Add-On",
        isShowing: !waitlist && !event.isSimple && filterOptions[ADD_ON_KIND].length > 0
      },
      badge: addOnBadge,
      component: AddOnOptions
    },
    [QUESTION_KIND]: {
      dropdown: {
        label: "Questions",
        isShowing: !waitlist && filterOptions[QUESTION_KIND].length > 0
      },
      badge: questionBadge,
      component: QuestionOptions
    },
    [FORM_KIND]: {
      dropdown: {
        label: "Form",
        isShowing: !waitlist && !event.isSimple && filterOptions[FORM_KIND].length > 0
      },
      badge: formBadge,
      component: FormOptions
    },
    [DISCOUNT_KIND]: {
      dropdown: {
        label: "Discount",
        isShowing: !waitlist && !event.isSimple && filterOptions[DISCOUNT_KIND].length > 0
      },
      badge: discountBadge,
      component: DiscountOptions
    },
    [SCHOLARSHIP_KIND]: {
      dropdown: {
        label: "Scholarship",
        isShowing: !waitlist && !event.isSimple && filterOptions[SCHOLARSHIP_KIND].length > 0
      },
      badge: scholarshipBadge,
      component: ScholarshipOptions
    },
    [ATTENDANCE_KIND]: {
      dropdown: {
        label: "Attendance",
        isShowing: !waitlist && filterOptions[ATTENDANCE_KIND].length > 0
      },
      badge: attendanceBadge,
      component: AttendanceOptions
    }
  }

  const handleFilterClear = () => {
    onFilterClear()
  }

  const handleFilterAdd = async search => {
    pendoTrackEvent("filter_by_selection", { category: search.kind, value: search.value })
    await onFilterAdd(search)
  }

  const handleFilterRemove = key => {
    onFilterRemove(key)
  }

  return (
    <div
      className="d-f@lg ai-c jc-sb"
      css={{ borderBottom: "1px solid var(--pane--border-color)" }}
    >
      <div className="f-1">
        <FilterBadges
          searches={searchFilters}
          filterOptions={filterOptions}
          onRemove={handleFilterRemove}
          onClear={handleFilterClear}
          filterMenuItems={filterMenuItems}
        >
          <FilterDropdown
            filterOptions={filterOptions}
            searches={searchFilters}
            onSubmit={handleFilterAdd}
            filterMenuItems={filterMenuItems}
            isRegisterForSeparateTimesEnabled={event.isRegisterForSeparateTimesEnabled}
          />
        </FilterBadges>
      </div>
      <div
        className="x-line as-s"
        css={{
          minWidth: 320,
          "--text-input--box-shadow": "none",
          borderBottom: "1px solid var(--pane--border-color)",
          marginBottom: -1,
          "@media (max-width: 959px)": {
            borderLeft: 0,
            borderTop: "1px solid var(--pane--border-color)"
          }
        }}
      >
        <FilterInput
          onSubmit={handleFilterAdd}
          onRemove={handleFilterRemove}
          selected={searchFilters[SEARCH_KIND] || {}}
        />
      </div>
    </div>
  )
}

FilterBar.propTypes = {
  event: shape({
    isSimple: bool,
    isRegisterForSeparateTimesEnabled: bool
  }),
  filterOptions: shape({
    assignment: array,
    addOn: array,
    question: array,
    form: array,
    discount: array,
    scholarship: array,
    attendanceReport: array
  }),
  searchFilters: shape({
    nameOrEmail: shape({})
  }),
  onFilterAdd: func,
  onFilterRemove: func,
  onFilterClear: func,
  waitlist: bool
}

export default FilterBar
